import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EMPTY, Subject, Subscription, catchError, debounceTime, switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CimSubstationService, GojsSchematicComponent, SubstationTemplateDto, SubstationTemplateService } from 'shared-lib';

@Component({
  selector: 'app-schema',
  templateUrl: './schema.component.html',
  styleUrls: ['./schema.component.scss'],
  standalone: false,
})
export class SchemaComponent implements OnInit, OnDestroy {
  @ViewChild(GojsSchematicComponent) schematic?: GojsSchematicComponent;

  loading = true;
  templates: SubstationTemplateDto[] = [];
  substations: any[] = [];
  selectedTemplate: SubstationTemplateDto | null = null;
  selectedSubstation: any | null = null;

  private search$ = new Subject<string>();
  private searchSub?: Subscription;

  constructor(
    private substationTemplateService: SubstationTemplateService,
    private cimSubstationService: CimSubstationService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.searchSub = this.search$.pipe(
      debounceTime(300),
      switchMap((searchTerm) => this.cimSubstationService.getAllForSelect({ templateId: this.selectedTemplate!.id!, searchTerm }).pipe(
        catchError(() => {
          this.toastr.error('Error occurred while fetching substations');
          return EMPTY;
        }),
      )),
    ).subscribe((response: any) => this.substations = response?.body?.data ?? []);

    this.substationTemplateService.getAllForSelect().subscribe({
      next: (response: any) => {
        this.templates = response?.body?.data ?? [];
        if (!this.templates.length) {
          this.loading = false;
          return;
        }
        this.selectedTemplate = this.templates[0];
        this.loadSubstations(true);
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Error occurred while fetching substation templates');
      },
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.schematic?.flushPendingSave();
  }

  onSearchChange(searchTerm: string): void {
    this.search$.next(searchTerm);
  }

  selectTemplate(template: SubstationTemplateDto): void {
    this.whenSchematicCanLeave(() => {
      this.selectedTemplate = template;
      this.selectedSubstation = null;
      this.loading = true;
      this.loadSubstations(false);
    });
  }

  selectSubstation(substation: any): void {
    this.whenSchematicCanLeave(() => this.selectedSubstation = substation);
  }

  // Unrepublished edits on a published schematic get the schematic's own exit dialog before switching.
  private whenSchematicCanLeave(action: () => void): void {
    if (this.schematic) this.schematic.requestLeave(action);
    else action();
  }

  private loadSubstations(selectFirst: boolean): void {
    this.cimSubstationService.getAllForSelect({ templateId: this.selectedTemplate!.id!, searchTerm: '' }).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.substations = response?.body?.data ?? [];
        if (selectFirst) this.selectedSubstation = this.substations[0] ?? null;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Error occurred while fetching substations');
      },
    });
  }
}
